import type { 
  AppError, 
  ErrorCode, 
  ErrorContext, 
  Result, 
  UserId,
  ValidationError 
} from '../types'

// Error severity levels
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical'

// Enhanced error class
export class EnhancedError extends Error {
  public readonly code: ErrorCode
  public readonly severity: ErrorSeverity
  public readonly context?: ErrorContext
  public readonly userId?: UserId
  public readonly requestId?: string
  public readonly timestamp: string
  public readonly retryable: boolean
  public readonly cause?: Error

  constructor(
    code: ErrorCode,
    message: string,
    options: {
      severity?: ErrorSeverity
      context?: ErrorContext
      userId?: UserId
      requestId?: string
      cause?: Error
      retryable?: boolean
    } = {}
  ) {
    super(message)
    this.name = 'EnhancedError'
    this.code = code
    this.severity = options.severity ?? 'medium'
    this.context = options.context
    this.userId = options.userId
    this.requestId = options.requestId
    this.timestamp = new Date().toISOString()
    this.retryable = options.retryable ?? false
    
    if (options.cause) {
      this.cause = options.cause
    }
  }

  toAppError(): AppError {
    return {
      code: this.code,
      message: this.message,
      timestamp: this.timestamp,
      userId: this.userId,
      requestId: this.requestId,
      context: this.context,
      stack: this.stack
    }
  }
}

// Error factory functions
export const createAuthError = (
  message: string = 'Authentication failed',
  context?: ErrorContext
): EnhancedError => {
  return new EnhancedError('AUTH_FAILED', message, {
    severity: 'high',
    context,
    retryable: false
  })
}

export const createValidationError = (
  message: string = 'Validation failed',
  validationErrors: ValidationError[] = [],
  context?: ErrorContext
): EnhancedError => {
  return new EnhancedError('VALIDATION_ERROR', message, {
    severity: 'low',
    context: {
      ...context,
      validationErrors
    },
    retryable: false
  })
}

export const createNetworkError = (
  message: string = 'Network request failed',
  context?: ErrorContext
): EnhancedError => {
  return new EnhancedError('NETWORK_ERROR', message, {
    severity: 'medium',
    context,
    retryable: true
  })
}

export const createServerError = (
  message: string = 'Internal server error',
  context?: ErrorContext
): EnhancedError => {
  return new EnhancedError('SERVER_ERROR', message, {
    severity: 'high',
    context,
    retryable: true
  })
}

export const createPermissionError = (
  message: string = 'Permission denied',
  context?: ErrorContext
): EnhancedError => {
  return new EnhancedError('PERMISSION_DENIED', message, {
    severity: 'medium',
    context,
    retryable: false
  })
}

export const createContentViolationError = (
  message: string = 'Content violates community guidelines',
  context?: ErrorContext
): EnhancedError => {
  return new EnhancedError('CONTENT_VIOLATION', message, {
    severity: 'high',
    context,
    retryable: false
  })
}

export const createAgeVerificationError = (
  message: string = 'Age verification required',
  context?: ErrorContext
): EnhancedError => {
  return new EnhancedError('AGE_VERIFICATION_REQUIRED', message, {
    severity: 'critical',
    context,
    retryable: false
  })
}

// Result utility functions
export const success = <T>(data: T): Result<T> => ({
  success: true,
  data
})

export const failure = <T>(error: AppError): Result<T> => ({
  success: false,
  error
})

export const fromPromise = async <T>(
  promise: Promise<T>,
  errorContext?: ErrorContext
): Promise<Result<T>> => {
  try {
    const data = await promise
    return success(data)
  } catch (error) {
    if (error instanceof EnhancedError) {
      return failure(error.toAppError())
    }
    
    if (error instanceof Error) {
      const enhancedError = new EnhancedError(
        'UNKNOWN_ERROR',
        error.message,
        { context: errorContext, cause: error }
      )
      return failure(enhancedError.toAppError())
    }
    
    const unknownError = new EnhancedError(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      { context: { ...errorContext, originalError: error } }
    )
    return failure(unknownError.toAppError())
  }
}

// Error boundary utilities
export const handleError = (error: Error | EnhancedError, context?: ErrorContext): void => {
  let appError: AppError

  if (error instanceof EnhancedError) {
    appError = error.toAppError()
  } else {
    const enhancedError = new EnhancedError(
      'UNKNOWN_ERROR',
      error.message,
      { context, cause: error }
    )
    appError = enhancedError.toAppError()
  }

  // Log error (in production, send to monitoring service)

  // Send to error reporting service in production
  if (import.meta.env.PROD) {
    reportError(appError)
  }
}

// Error reporting (mock - replace with actual service)
const reportError = (error: AppError): void => {
  // In production, send to error reporting service like Sentry

}

// Retry utility with exponential backoff
export const withRetry = async <T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number
    baseDelay?: number
    maxDelay?: number
    backoffFactor?: number
    shouldRetry?: (error: Error) => boolean
  } = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    shouldRetry = (error) => {
      if (error instanceof EnhancedError) {
        return error.retryable
      }
      return false
    }
  } = options

  let lastError: Error
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      
      if (attempt === maxRetries || !shouldRetry(lastError)) {
        throw lastError
      }
      
      const delay = Math.min(
        baseDelay * Math.pow(backoffFactor, attempt),
        maxDelay
      )
      
      // Add jitter to prevent thundering herd
      const jitteredDelay = delay * (0.5 + Math.random() * 0.5)
      
      await new Promise(resolve => setTimeout(resolve, jitteredDelay))
    }
  }
  
  throw lastError!
}

// Circuit breaker pattern
export class CircuitBreaker<T extends unknown[], R> {
  private failures = 0
  private lastFailureTime?: number
  private state: 'closed' | 'open' | 'half-open' = 'closed'

  constructor(
    private operation: (...args: T) => Promise<R>,
    private options: {
      failureThreshold: number
      resetTimeout: number
      monitoringPeriod: number
    }
  ) {}

  async execute(...args: T): Promise<R> {
    if (this.state === 'open') {
      if (this.shouldAttemptReset()) {
        this.state = 'half-open'
      } else {
        throw new EnhancedError(
          'SERVER_ERROR',
          'Circuit breaker is open - service unavailable',
          { severity: 'high', retryable: true }
        )
      }
    }

    try {
      const result = await this.operation(...args)
      this.onSuccess()
      return result
    } catch (error) {
      this.onFailure()
      throw error
    }
  }

  private shouldAttemptReset(): boolean {
    return this.lastFailureTime !== undefined &&
           Date.now() - this.lastFailureTime >= this.options.resetTimeout
  }

  private onSuccess(): void {
    this.failures = 0
    this.state = 'closed'
    this.lastFailureTime = undefined
  }

  private onFailure(): void {
    this.failures++
    this.lastFailureTime = Date.now()
    
    if (this.failures >= this.options.failureThreshold) {
      this.state = 'open'
    }
  }

  getState(): { state: string; failures: number } {
    return {
      state: this.state,
      failures: this.failures
    }
  }
}

// Error aggregation for batch operations
export class ErrorAggregator {
  private errors: AppError[] = []

  addError(error: AppError | EnhancedError): void {
    if (error instanceof EnhancedError) {
      this.errors.push(error.toAppError())
    } else {
      this.errors.push(error)
    }
  }

  hasErrors(): boolean {
    return this.errors.length > 0
  }

  getErrors(): AppError[] {
    return [...this.errors]
  }

  clear(): void {
    this.errors = []
  }

  createSummaryError(): EnhancedError {
    const errorCounts = this.errors.reduce((counts, error) => {
      counts[error.code] = (counts[error.code] || 0) + 1
      return counts
    }, {} as Record<ErrorCode, number>)

    const message = `Multiple errors occurred: ${Object.entries(errorCounts)
      .map(([code, count]) => `${code}(${count})`)
      .join(', ')}`

    return new EnhancedError('UNKNOWN_ERROR', message, {
      severity: 'high',
      context: {
        errorCounts,
        totalErrors: this.errors.length
      }
    })
  }
}

// Type-safe error handling for React components
export const useErrorHandler = () => {
  const handleError = (error: Error | EnhancedError, context?: ErrorContext) => {
    if (error instanceof EnhancedError) {
      // Handle based on error type and severity
      switch (error.severity) {
        case 'critical':
          // Show critical error UI, potentially redirect
          break
        case 'high':
          // Show error toast/modal
          break
        case 'medium':
          // Show warning notification
          break
        case 'low':
          // Log silently or show subtle indication
          break
      }
    }
    
    // Always log the error
    handleError(error, context)
  }

  return { handleError }
}